import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { API_BASE } from '../../config';
import { db } from '../../firebase';
import RichContent from '../common/RichContent';
import { slugify } from '../../utils/slugify';
import { stripMarkdown } from '../../utils/stripMarkdown';
import { autoFormatMarkdownFromPlainText } from '../../utils/autoFormatMarkdown';

function normaliseWhitespace(value) {
  return String(value || '').replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function AdminSlotReviewTool() {
  const [slotName, setSlotName] = useState('');
  const [provider, setProvider] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [generated, setGenerated] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);

  const canGenerate = useMemo(() => normaliseWhitespace(sourceText).length >= 40, [sourceText]);
  const canSave = useMemo(() => normaliseWhitespace(generated).length >= 80, [generated]);

  useEffect(() => {
    const q = query(
      collection(db, 'slotReviews'),
      orderBy('createdAt', 'desc'),
      limit(6)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRecent(items);
      },
      () => {
        setRecent([]);
      }
    );

    return () => unsub();
  }, []);

  const handleGenerate = async () => {
    if (!API_BASE) {
      setStatus('API base URL is not configured. Set REACT_APP_API_BASE_URL in your env.');
      return;
    }

    const rawText = normaliseWhitespace(sourceText);
    if (rawText.length < 40) {
      setStatus('Paste at least a few sentences to rewrite (40+ characters).');
      return;
    }

    setLoading(true);
    setStatus('Asking the AI to rewrite your slot review…');
    setGenerated('');

    try {
      const resp = await fetch(`${API_BASE}/api/ai/slot-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotName: normaliseWhitespace(slotName) || undefined,
          provider: normaliseWhitespace(provider) || undefined,
          text: rawText,
        }),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'AI API returned non-200 status');
      }

      const data = await resp.json();
      const formatted = autoFormatMarkdownFromPlainText(data.content || '');
      setGenerated(normaliseWhitespace(formatted || data.content || ''));
      setStatus('Done. Review rewritten.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Slot review generation failed', err);
      setStatus('Failed to generate slot review. Check API logs / console.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generated || '');
      setStatus('Copied to clipboard.');
    } catch {
      setStatus('Copy failed (browser permission). You can still select + copy manually.');
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setStatus('Saving slot review…');

    try {
      const title = normaliseWhitespace(slotName) || 'Slot review';
      const slug = slugify(title) || 'slot-review';

      // Use slug as the document ID so URLs are clean: /slots/{slug}
      const ref = doc(db, 'slotReviews', slug);
      const existing = await getDoc(ref);
      if (existing.exists()) {
        setStatus(
          'A review with this title already exists (same URL). Change the title to make a different URL.'
        );
        return;
      }

      await setDoc(ref, {
        title,
        provider: normaliseWhitespace(provider) || '',
        imageUrl: normaliseWhitespace(imageUrl) || '',
        sourceText: normaliseWhitespace(sourceText) || '',
        body: normaliseWhitespace(autoFormatMarkdownFromPlainText(generated) || generated) || '',
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setStatus('Saved to Firestore (slotReviews).');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Saving slot review failed', err);
      setStatus('Could not save. Check Firestore rules / console.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="content-card slot-review-card" aria-label="Slot review AI rewrite tool">
      <div className="content-card-header">
        <div>
          <div className="section-title">Slot review AI</div>
          <h2 className="section-subtitle">Rewrite your slot review text</h2>
        </div>
      </div>

      <p className="secondary-text" style={{ marginTop: 4 }}>
        Paste your rough notes and click “Create review”. This does not auto-post; you decide what to save.
      </p>

      <div className="mini-list admin-form" style={{ marginTop: 12 }}>
        <div className="mini-item">
          <label className="mini-label" htmlFor="slotName">
            Slot name
          </label>
          <input
            id="slotName"
            className="text-input"
            type="text"
            placeholder="e.g. Bigger Bass Bonanza"
            value={slotName}
            onChange={(e) => setSlotName(e.target.value)}
          />
        </div>

        <div className="mini-item">
          <label className="mini-label" htmlFor="provider">
            Provider / studio
          </label>
          <input
            id="provider"
            className="text-input"
            type="text"
            placeholder="e.g. Pragmatic Play"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          />
        </div>

        <div className="mini-item" style={{ gridColumn: '1 / -1' }}>
          <label className="mini-label" htmlFor="imageUrl">
            Image URL (thumbnail / featured image)
          </label>
          <input
            id="imageUrl"
            className="text-input"
            type="url"
            placeholder="https://…"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </div>

        <div className="mini-item" style={{ gridColumn: '1 / -1' }}>
          <label className="mini-label" htmlFor="sourceText">
            Your raw text
          </label>
          <textarea
            id="sourceText"
            className="text-input"
            rows={7}
            placeholder="Paste your rough review notes here…"
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
          />
        </div>
      </div>

      <div className="slot-review-actions" style={{ marginTop: 14 }}>
        <button type="button" className="cta-button" onClick={handleGenerate} disabled={loading || !canGenerate}>
          <span>{loading ? '…' : '+'}</span> {loading ? 'Creating…' : 'Create for me Review for iGaming Slot'}
        </button>
        <button type="button" className="header-outline-button" onClick={handleCopy} disabled={!generated}>
          Copy
        </button>
        <button
          type="button"
          className="header-outline-button"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {status && (
        <p className="secondary-text" style={{ marginTop: 10 }}>
          {status}
        </p>
      )}

      {generated && (
        <div className="slot-review-output" style={{ marginTop: 14 }}>
          <div className="sidebar-title">Rewritten review</div>
          <RichContent content={generated} forceAutoFormat />
        </div>
      )}

      {!!recent.length && (
        <div style={{ marginTop: 16 }}>
          <div className="sidebar-title">Recent saved reviews</div>
          <div className="mini-list">
            {recent.map((item) => (
              <details key={item.id} className="mini-item">
                <summary className="mini-label" style={{ cursor: 'pointer' }}>
                  {String(item.title || 'Slot review')} {item.provider ? `· ${item.provider}` : ''}
                </summary>
                <div className="secondary-text" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                  {stripMarkdown(String(item.body || '')).slice(0, 1200)}
                  {stripMarkdown(String(item.body || '')).length > 1200 ? '…' : ''}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default AdminSlotReviewTool;


